import React, { useState, useEffect } from "react";
import { useUser } from '../../Hooks/useUser';
import useData from '../../Hooks/useData';
import { DateTime } from "luxon";
import axios from '../../api/axios';
import { printUserProfile } from './ProfilePrint';
import { useMediaQuery } from '@mui/material';

const ProfileHeader = () => {
    const user = useUser();
    const { auth } = useData();
    const isMobile = useMediaQuery("(max-width:600px)");
    const [profilePicture, setProfilePicture] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);

    const userObj = user?.user || user || {};
    const _createdDt = DateTime.fromISO(userObj?.UserCreated);
    const createDate = _createdDt.isValid ? _createdDt.toFormat('MMMM d, yyyy') : 'N/A';
    const id        = userObj?.UserID;
    const lastName  = userObj?.LastName  || '';
    const firstName = userObj?.FirstName || '';

    useEffect(() => {
        if (!userObj?.UserID) return;
        axios.get(`/api/users/alluserinfo/${userObj.UserID}`)
            .then(response => {
                const pic = response.data?.userSettings?.UserPicture;
                if (pic) setProfilePicture(pic);
            })
            .catch(() => {});
    }, [userObj?.UserID]);

    const handlePrintProfile = async () => {
        if (!userObj?.UserID) return;
        setIsPrinting(true);
        try {
            await printUserProfile(userObj.UserID, auth?.token);
        } finally {
            setIsPrinting(false);
        }
    };

    if (!user) return <div />;

    return (
        <div className="full-row light-blue">
            <div className="container-fluid">
                <div
                    id="account-profile"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '40px 16px',
                        gap: '16px',
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Left: photo + details */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {profilePicture ? (
                            <img
                                src={profilePicture}
                                alt={`${firstName} ${lastName}`}
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '3px solid #fff',
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <div
                                className="profile-img-lg"
                                data-initials={`${firstName.charAt(0)}${lastName.charAt(0)}`}
                                style={{ margin: '0', flexShrink: 0 }}
                            />
                        )}
                        <div className="text-white">
                            <h4 style={{ margin: '0 0 4px 0' }}>{firstName} {lastName}</h4>
                            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
                                Member Since: {createDate}
                                <span className="spacer" />
                                Member ID: {id}
                            </p>
                        </div>
                    </div>

                    {/* Right: Print Profile button */}
                    <button
                        onClick={handlePrintProfile}
                        disabled={isPrinting}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#003063',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            fontFamily: 'proxima-nova, sans-serif',
                            fontWeight: '700',
                            fontSize: '13px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            cursor: isPrinting ? 'not-allowed' : 'pointer',
                            opacity: isPrinting ? 0.7 : 1,
                            transition: 'background 300ms ease',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            margin: isMobile ? '0 auto' : 0,
                        }}
                        onMouseEnter={e => { if (!isPrinting) e.currentTarget.style.backgroundColor = '#002145'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#003063'; }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                            viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                        </svg>
                        {isPrinting ? 'Preparing…' : 'Print Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileHeader;
